import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import { AdminStateModel } from './admin.model'
import { AdminService } from '../admin.service'
import {
  AdminBulkEditLocations,
  AdminBulkEditPermissions,
  AdminBulkInsertPermissions,
  AdminGetAllPermissions,
  AdminGetUsersList,
  AdminGetWeeklyTires,
  AdminGetUserPermissions,
  AdminUpdateWeeklyTires,
  AdminUpdateUserPermissions,
  GetAdminLocationsList,
  AdminGetLocation,
  AdminUpdateLocation,
  AdminOverrideWeeklyTires,
  AdminOverrideMonthlyTargets,
  AdminGetProtractorLocationsList,
  AdminCreateLocation,
  AdminGetDailyReports,
  AdminImportDailyReports,
  AdminImportPreLLAInvoices,
  AdminPostQueueMessage,
  AdminGetAttentionProposals,
  AdminApplyAttentionProposals
} from './admin.actions'

@State<AdminStateModel>({
  name: 'admin'
})
@Injectable()
export class AdminState {
  constructor(private adminService: AdminService) {}

  @Selector()
  static shopTiers(state: AdminStateModel) {
    return state.shopTiers
  }

  @Selector()
  static protractorLocations(state: AdminStateModel) {
    return state.protractorLocations
  }

  @Selector()
  static usersListResponse(state: AdminStateModel) {
    return state.usersListResponse
  }

  @Selector()
  static userPermissions(state: AdminStateModel) {
    return state.userPermissions
  }

  @Selector()
  static allPermissions(state: AdminStateModel) {
    return state.allPermissions
  }

  @Selector()
  static attentionProposals(state: AdminStateModel) {
    return state.attentionProposals
  }

  @Selector()
  static locationsListResponse(state: AdminStateModel) {
    return state.locationsListResponse
  }

  @Selector()
  static locationInfo(state: AdminStateModel) {
    return state.locationInfo
  }

  @Selector()
  static dailyReports(state: AdminStateModel) {
    return state.dailyReports
  }

  @Action(AdminGetWeeklyTires)
  adminGetWeeklyTires(ctx: StateContext<AdminStateModel>) {
    ctx.patchState({
      shopTiers: undefined
    })
    return this.adminService.getWeeklyTires().pipe(
      tap((res) => {
        ctx.patchState({
          shopTiers: res?.locations ?? []
        })
      })
    )
  }

  @Action(AdminUpdateWeeklyTires)
  adminUpdateWeeklyTires(ctx: StateContext<AdminStateModel>, { payload }: AdminUpdateWeeklyTires) {
    return this.adminService.updateWeeklyTires(payload)
  }

  @Action(AdminOverrideWeeklyTires)
  adminOverrideWeeklyTires(ctx: StateContext<AdminStateModel>) {
    return this.adminService.overrideWeeklyTires()
  }

  @Action(AdminOverrideMonthlyTargets)
  adminOverrideMonthlyTargets(ctx: StateContext<AdminStateModel>) {
    return this.adminService.overrideMonthlyTargets()
  }

  @Action(AdminGetProtractorLocationsList)
  adminGetProtractorLocationsList(ctx: StateContext<AdminStateModel>) {
    ctx.patchState({
      protractorLocations: undefined
    })
    return this.adminService.getProtractorLocationsList().pipe(
      tap((res) => {
        ctx.patchState({
          protractorLocations: res?.locations ?? []
        })
      })
    )
  }

  @Action(AdminGetUsersList)
  adminGetUsersList(ctx: StateContext<AdminStateModel>, { info, top }: AdminGetUsersList) {
    ctx.patchState({
      usersListResponse: undefined
    })
    return this.adminService.getUsersList(info, top).pipe(
      tap((res) => {
        ctx.patchState({
          usersListResponse: res
        })
      })
    )
  }

  @Action(AdminGetUserPermissions)
  adminGetUserPermissions(ctx: StateContext<AdminStateModel>, { userId }: AdminGetUserPermissions) {
    return this.adminService.getUserPermissions(userId).pipe(
      tap((res) => {
        ctx.patchState({
          userPermissions: res
        })
      })
    )
  }

  @Action(AdminUpdateUserPermissions)
  adminUpdateUserPermissions(
    ctx: StateContext<AdminStateModel>,
    { userId, payload }: AdminUpdateUserPermissions
  ) {
    return this.adminService.updateUserPermissions(userId, payload)
  }

  @Action(AdminGetAllPermissions)
  adminGetAllPermissions(ctx: StateContext<AdminStateModel>) {
    const allPermissions = ctx.getState()?.allPermissions
    if (allPermissions) {
      return allPermissions
    }

    ctx.patchState({
      allPermissions: undefined
    })
    return this.adminService.getAllPermissions().pipe(
      tap((res) => {
        ctx.patchState({
          allPermissions: res
        })
      })
    )
  }

  @Action(AdminBulkEditLocations)
  adminBulkEditLocations(ctx: StateContext<AdminStateModel>, { payload }: AdminBulkEditLocations) {
    return this.adminService.bulkEditLocations(payload)
  }

  @Action(AdminBulkEditPermissions)
  adminBulkEditPermissions(ctx: StateContext<AdminStateModel>, { payload }: AdminBulkEditPermissions) {
    return this.adminService.bulkEditPermissions(payload)
  }

  @Action(AdminBulkInsertPermissions)
  adminBulkInsertPermissions(
    ctx: StateContext<AdminStateModel>,
    { payload }: AdminBulkInsertPermissions
  ) {
    return this.adminService.bulkInsertPermissions(payload)
  }

  @Action(GetAdminLocationsList)
  getAdminLocationsList(ctx: StateContext<AdminStateModel>, { info }: GetAdminLocationsList) {
    ctx.patchState({
      locationsListResponse: undefined
    })
    return this.adminService.getAdminLocationsList(info).pipe(
      tap((res) => {
        ctx.patchState({
          locationsListResponse: res
        })
      })
    )
  }

  @Action(AdminGetLocation)
  adminGetLocation(ctx: StateContext<AdminStateModel>, { locationId }: AdminGetLocation) {
    return this.adminService.adminGetLocation(locationId).pipe(
      tap((res) => {
        ctx.patchState({
          locationInfo: res
        })
      })
    )
  }

  @Action(AdminUpdateLocation)
  adminUpdateLocation(ctx: StateContext<AdminStateModel>, { locationId, payload }: AdminUpdateLocation) {
    return this.adminService.adminUpdateLocation(locationId, payload)
  }

  @Action(AdminCreateLocation)
  adminCreateLocation(ctx: StateContext<AdminStateModel>, { payload }: AdminCreateLocation) {
    return this.adminService.adminCreateLocation(payload)
  }

  @Action(AdminGetDailyReports)
  adminGetDailyReports(ctx: StateContext<AdminStateModel>, { locationId }: AdminGetDailyReports) {
    ctx.patchState({
      dailyReports: undefined
    })
    return this.adminService.adminGetDailyReports(locationId).pipe(
      tap((res) => {
        ctx.patchState({
          dailyReports: res
        })
      })
    )
  }

  @Action(AdminImportDailyReports)
  adminImportDailyReports(
    ctx: StateContext<AdminStateModel>,
    { locationId, payload }: AdminImportDailyReports
  ) {
    return this.adminService.adminImportDailyReports(locationId, payload)
  }

  @Action(AdminImportPreLLAInvoices)
  adminImportPreLLAInvoices(
    ctx: StateContext<AdminStateModel>,
    { locationId, payload }: AdminImportPreLLAInvoices
  ) {
    return this.adminService.adminImportPreLLAInvoices(locationId, payload)
  }

  @Action(AdminPostQueueMessage)
  adminPostQueueMessage(ctx: StateContext<AdminStateModel>, { payload }: AdminPostQueueMessage) {
    return this.adminService.postQueueMessage(payload)
  }

  @Action(AdminGetAttentionProposals)
  adminGetAttentionProposals(ctx: StateContext<AdminStateModel>) {
    ctx.patchState({
      attentionProposals: undefined
    })
    return this.adminService.adminGetAttentionProposals().pipe(
      tap((res) => {
        ctx.patchState({ attentionProposals: res?.locations ?? [] })
      })
    )
  }

  @Action(AdminApplyAttentionProposals)
  adminApplyAttentionProposals(
    ctx: StateContext<AdminStateModel>,
    { payload }: AdminApplyAttentionProposals
  ) {
    return this.adminService.adminApplyAttentionProposals(payload)
  }
}
